<template>
  <form @submit.prevent="submit" autocomplete="off">
    <p class="mb-5">
      {{
        this.$t(
          "Give your report a name, select a report type and choose how often you want to send it. Then add the email address(es) to send the report to."
        )
      }}
    </p>
    <b-row class="mb-3">
      <b-col :sm="!this.oneTime ? '6' : '12'">
        <b-form-group
          :label="this.$t('Report Name')"
          label-for="subject"
          :state="$validateState($v.form.subject)"
        >
          <b-form-input
            autofocus
            data-test-id="modal-report-name"
            :state="$validateState($v.form.subject)"
            :placeholder="this.$t('For example, Weekly Blocked Domains')"
            id="subject"
            @blur="setValidationInput('subject', $event.target.value)"
          />

          <template #invalid-feedback>
            <p v-if="!$v.form.subject.required">
              {{ $t("This is a required field.") }}
            </p>

            <p
              v-if="
                $v.form.subject.$dirty &&
                $v.form.subject.required &&
                !$v.form.subject.minLength
              "
            >
              {{
                $t("X must be at least X characters long.", {
                  field: $t("Name"),
                  length: $v.form.subject.$params.minLength.min,
                })
              }}
            </p>
          </template>
        </b-form-group>
      </b-col>

      <b-col sm="6" v-if="!this.oneTime">
        <b-form-group
          :label="this.$t('Report Type')"
          label-for="report"
          :state="$validateState($v.form.report)"
        >
          <b-form-select
            data-test-id="modal-report-type"
            :state="$validateState($v.form.report)"
            id="report"
            @change="setValidationInput('report', $event)"
            v-model="$v.form.report.$model"
            :options="$reportTypes"
          ></b-form-select>

          <template #invalid-feedback>
            <p v-if="!$v.form.report.required">
              {{ $t("This is a required field.") }}
            </p>
          </template>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mb-3">
      <b-col sm="12">
        <b-form-group :label="this.$t('Frequency')" label-for="frequency">
          <b-form-radio-group
            data-test-id="modal-report-frequency"
            id="frequency"
            v-model="form.frequency"
            :options="frequencyOptions"
            @change="frequencyChange"
            button-variant="outline-secondary"
            class="d-flex"
            size="lg"
            buttons
          ></b-form-radio-group>
        </b-form-group>
        <p>
          {{ frequencyInfoText }}
        </p>
      </b-col>
    </b-row>

    <b-row v-if="!this.oneTime">
      <b-col sm="12">
        <b-form-group
          :label="this.$t('Recipient(s)')"
          label-for="recipients"
          :state="$validateState($v.form.recipients)"
        >
          <b-form-tags
            tag-pills
            data-test-id="modal-report-recipients"
            input-id="recipients"
            separator=" "
            :placeholder="this.$t('Add Email Address(es)...')"
            :tag-validator="email"
            :state="$validateState($v.form.recipients)"
            @input="setValidationInput('recipients', $event)"
            :invalid-tag-text="$t('Must be valid email address')"
          ></b-form-tags>

          <template #invalid-feedback>
            <p v-if="!$v.form.subject.required">
              {{ $t("This is a required field.") }}
            </p>
          </template>
        </b-form-group>
      </b-col>
    </b-row>
  </form>
</template>

<script>
import { mapMutations, mapGetters, mapActions } from "vuex";
import {
  required,
  requiredIf,
  email,
  minLength,
} from "vuelidate/lib/validators";

export default {
  data() {
    return {
      oneTime: false,
      form: {
        report: null,
        recipients: [],
        subject: null,
        frequency: "weekly",
      },
      frequencyOptions: [
        { value: "one_time", text: this.$t("One off") },
        { value: "daily", text: this.$t("Daily") },
        { value: "weekly", text: this.$t("Weekly") },
        { value: "monthly", text: this.$t("Monthly") },
      ],
      frequencyInfoTextMap: {
        one_time: this.$t(
          "Click Create Report to select options for a one off PDF report."
        ),
        daily: this.$t("Run the report every day."),
        weekly: this.$t("Run the report weekly each Monday."),
        monthly: this.$t(
          "Run the report monthly on the 1st day of each month."
        ),
      },
    };
  },
  validations: {
    form: {
      report: {
        required: requiredIf(function () {
          return !this.checkOneTime;
        }),
      },
      recipients: {
        required: requiredIf(function () {
          return !this.checkOneTime;
        }),
      },
      subject: {
        required,
        minLength: minLength(5),
      },
    },
  },
  methods: {
    ...mapMutations("modal", ["userAction", "showModal"]),
    ...mapActions("scheduledReports", [
      "createScheduledReport",
      "createOneTimeReport",
    ]),
    email,
    frequencyChange() {
      this.oneTime = this.form.frequency === "one_time";
    },
    setValidationInput(object, value) {
      this.form[`${object}`] = value;
      this.$v.form[`${object}`].$touch();
    },
    async submit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        if (this.oneTime) {
          await this.createOneTimeReport({
            id: 3,
            subject: this.form.subject,
          });
          this.showModal(false);
          this.$router.push("/customer/reporting/reports/one_time");
        } else {
          const recipients = this.form.recipients.join();
          await this.createScheduledReport({
            id: this.selectedUser.id,
            scheduledReport: {
              ...this.form,
              recipients,
              sender: this.selectedUser.email,
            },
          });

          this.showModal(false);
        }
      }
    },
  },
  computed: {
    ...mapGetters("authentication", ["selectedUser"]),
    frequencyInfoText() {
      return this.frequencyInfoTextMap[this.form.frequency];
    },
    checkOneTime() {
      return this.oneTime;
    },
  },
  created() {
    this.userAction(this.submit);
  },
};
</script>

<style lang="scss" scoped>
::v-deep .btn-group.btn-group {
  background-color: white;
  label {
    border-color: $border-color;
    color: $body-color;
    font-size: 16px;

    &.active,
    &:hover {
      background-color: #d9eeec;
      border-color: $secondary;
      color: $secondary;
    }
    &.active {
      background-color: #d9eeec;
      border-color: $secondary;
      color: $secondary;
      font-weight: bold;
    }
  }
}
</style>
